import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { brandsApi } from '../../services/apiService';
import { SOCKET_URL } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import './AllBrandsPage.css';

const AllBrandsPage = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const brandsPerPage = 120; // 12 rows x 10 brands per row = 120 brands per page

  // Fetch brands from API
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        const response = await brandsApi.getAll();
        setBrands(response);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBrands();
  }, []);

  const totalPages = Math.ceil(brands.length / brandsPerPage);
  const startIndex = (currentPage - 1) * brandsPerPage;
  const currentBrands = brands.slice(startIndex, startIndex + brandsPerPage);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="all-brands-page">
      <Header />
      
      <main className="brands-main-content">
        <div className="brands-container">
          {/* Breadcrumb */}
          <div className="brands-breadcrumb">
            <Link to="/" className="breadcrumb-link">Home</Link>
            <svg className="breadcrumb-chevron" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 13L10 9L6 5" stroke="#505050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="breadcrumb-current">All Brand</span>
          </div>

          {/* Page Title */}
          <div className="brands-header">
            <h1 className="brands-title">All Brand</h1>
            <p className="brands-subtitle">Explore trusted brands across every category in one place.</p>
          </div>

          {/* Brands Grid */}
          <div className="brands-grid-section">
            {loading ? (
              <div className="loading-placeholder">Loading brands...</div>
            ) : (
              <div className="brands-grid">
                {currentBrands.map((brand) => {
                  // Generate initials and color for placeholder
                  const initials = brand.name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                  const colors = ['#FF5733', '#3357FF', '#33FF57', '#FF33F5', '#F5A533', '#33FFF5', '#FF8C33', '#8C33FF', '#009688'];
                  const bgColor = colors[brand.id % colors.length];
                  
                  // If brand has a logo URL, show image; otherwise show initials
                  if (brand.logo) {
                    return (
                      <div 
                        key={brand.id} 
                        className="brand-item" 
                        title={`View products from ${brand.name}`}
                        onClick={() => navigate(`/products?brand=${brand.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <img 
                          src={`${SOCKET_URL}${brand.logo}`} 
                          alt={brand.name}
                          className="brand-logo"
                          loading="eager"
                          onLoad={(e) => {
                            // Detect tiny/corrupt images (e.g. 1x1 pixel test images)
                            if (e.target.naturalWidth < 10 || e.target.naturalHeight < 10) {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:${bgColor};border-radius:50%;color:#fff;font-weight:bold;font-size:28px;">${initials}</div>`;
                            }
                          }}
                          onError={(e) => { 
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:${bgColor};border-radius:50%;color:#fff;font-weight:bold;font-size:28px;">${initials}</div>`;
                          }}
                        />
                      </div>
                    );
                  } else {
                    // No logo - show initials placeholder
                    return (
                      <div 
                        key={brand.id} 
                        className="brand-item" 
                        title={`View products from ${brand.name}`}
                        onClick={() => navigate(`/products?brand=${brand.id}`)}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: bgColor,
                          borderRadius: '50%',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '28px'
                        }}>
                          {initials}
                        </div>
                      </div>
                    );
                  }
                })}
              </div>
            )}
          </div>

          {/* Pagination */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AllBrandsPage;
